// =============================================================================
// Dashboard data layer — calls the dashboard_* aggregation RPCs (POS invoices +
// contractor bills) plus a few lightweight "quick stat" head-count queries.
// All ranges are half-open [start, end) ISO timestamps.
// =============================================================================
import { supabase } from './supabase/client'

export interface DashboardSummary {
  pos_count: number
  pos_sales: number
  pos_tax: number
  contractor_count: number
  contractor_sales: number
  contractor_tax: number
}

export interface TopCustomerRow {
  customer_id: string | null
  customer_name: string
  pos_sales: number
  contractor_sales: number
  total_sales: number
  txn_count: number
}

export interface MonthlyCombinedRow {
  month: string
  pos_sales: number
  contractor_sales: number
}

export interface QuickStats {
  appointmentsToday: number
  appointmentsThisWeek: number
  openEstimates: number
  openBills: number
}

const EMPTY_SUMMARY: DashboardSummary = {
  pos_count: 0, pos_sales: 0, pos_tax: 0, contractor_count: 0, contractor_sales: 0, contractor_tax: 0,
}

export async function dashboardSummary(start: string, end: string): Promise<DashboardSummary> {
  const { data, error } = await supabase.rpc('dashboard_summary', { start_date: start, end_date: end })
  if (error) throw error
  const row = (data ?? [])[0] as Partial<DashboardSummary> | undefined
  if (!row) return EMPTY_SUMMARY
  // Numerics come back as strings over the wire — coerce to numbers.
  return {
    pos_count: Number(row.pos_count ?? 0),
    pos_sales: Number(row.pos_sales ?? 0),
    pos_tax: Number(row.pos_tax ?? 0),
    contractor_count: Number(row.contractor_count ?? 0),
    contractor_sales: Number(row.contractor_sales ?? 0),
    contractor_tax: Number(row.contractor_tax ?? 0),
  }
}

export async function dashboardTopCustomers(start: string, end: string, limit = 10): Promise<TopCustomerRow[]> {
  const { data, error } = await supabase.rpc('dashboard_top_customers', { start_date: start, end_date: end, row_limit: limit })
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    customer_id: (r.customer_id as string) ?? null,
    customer_name: (r.customer_name as string) ?? 'Unknown',
    pos_sales: Number(r.pos_sales ?? 0),
    contractor_sales: Number(r.contractor_sales ?? 0),
    total_sales: Number(r.total_sales ?? 0),
    txn_count: Number(r.txn_count ?? 0),
  }))
}

export async function dashboardMonthlyCombined(start: string, end: string): Promise<MonthlyCombinedRow[]> {
  const { data, error } = await supabase.rpc('dashboard_monthly_combined', { start_date: start, end_date: end })
  if (error) throw error
  return (data ?? []).map((r: Record<string, unknown>) => ({
    month: r.month as string,
    pos_sales: Number(r.pos_sales ?? 0),
    contractor_sales: Number(r.contractor_sales ?? 0),
  }))
}

// Local YYYY-MM-DD (avoids UTC off-by-one from toISOString()).
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export async function dashboardQuickStats(): Promise<QuickStats> {
  const today = new Date()
  const todayStr = ymd(today)
  // Monday-anchored week range.
  const day = today.getDay()
  const mon = new Date(today); mon.setDate(today.getDate() - (day === 0 ? 6 : day - 1))
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6)

  const [apptToday, apptWeek, openEst, openBills] = await Promise.all([
    supabase.from('appointments').select('*', { count: 'exact', head: true }).eq('appointment_date', todayStr),
    supabase.from('appointments').select('*', { count: 'exact', head: true })
      .gte('appointment_date', ymd(mon)).lte('appointment_date', ymd(sun)),
    supabase.from('estimates').select('*', { count: 'exact', head: true }).is('invoice_number', null),
    supabase.from('bills').select('*', { count: 'exact', head: true }).gt('remaining_balance', 0),
  ])

  return {
    appointmentsToday: apptToday.count ?? 0,
    appointmentsThisWeek: apptWeek.count ?? 0,
    openEstimates: openEst.count ?? 0,
    openBills: openBills.count ?? 0,
  }
}
